import React, { HTMLAttributes, useRef, useState, useEffect, RefObject } from 'react';
import clx from 'classnames';

import useAccordionAnimationState from './hooks/useAccordionAnimationState';
import useAccordionHeight from './hooks/useAccordionHeight';

export type AccordionElementRef = RefObject<HTMLDivElement>;

interface Props extends HTMLAttributes<HTMLElement> {
    isOpen: boolean;
    duration: number;
    as?: 'div' | 'ul';
    children: React.ReactNode;
    style?: React.CSSProperties;
    openedCallback?: (ref: AccordionElementRef) => void; // Must be inside a useCallback.
    closedCallback?: () => void; // Must be inside a useCallback.
}

/**
 * Component that animates up and down height of an element.
 */
const AccordionAnimation = ({
    isOpen,
    duration,
    children,
    openedCallback,
    closedCallback,
    style,
    className,
    as = 'div',
    ...rest
}: Props) => {
    // If the component should be loaded in the DOM.
    const [isMounted, setIsMounted] = useState(false);

    // Manages the animation state of the accordion.
    const { animationState, startOpenAnimation, startCloseAnimation } = useAccordionAnimationState(duration);

    // Calculates the height the accordion needs to be set at based on the animation state.
    const htmlElementRef = useRef(null);
    const height = useAccordionHeight(animationState, htmlElementRef);

    const styles = {
        ...style,
        transitionDuration: `${duration}ms`,
        height,
    };

    const Tag = as;
    const classNames = clx(className, 'transition-height overflow-hidden');

    // Trigger the open or close animations depending on the isOpen prop when it changes.
    const loaded = useRef(false);
    useEffect(() => {
        // We don't want to run the close animation by default on load so we wait for the first render to
        // finish.
        if (!loaded.current) {
            loaded.current = true;
            return;
        }

        if (isOpen) {
            setIsMounted(true);
            startOpenAnimation();
            return;
        }

        startCloseAnimation();
    }, [isOpen, startOpenAnimation, startCloseAnimation, openedCallback]);

    // Used to unmount the component and trigger any callbacks.
    useEffect(() => {
        if (animationState === 'OPEN') {
            openedCallback?.(htmlElementRef);
        }

        if (animationState === 'CLOSED') {
            closedCallback?.();
            setIsMounted(false);
        }
    }, [animationState, closedCallback, openedCallback]);

    return isMounted ? (
        <Tag {...rest} className={classNames} style={styles} ref={htmlElementRef}>
            {children}
        </Tag>
    ) : null;
};

export default AccordionAnimation;
